/** @format */

import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  FaArrowCircleDown,
  FaArrowCircleUp,
  FaPauseCircle,
  FaCheck,
  FaAngleDown,
  FaAngleUp,
} from "react-icons/fa";
import { FaCircleInfo, FaCopy } from "react-icons/fa6";
import styles from "../../../styles/Components/trading-assistant/TradeAssistantTable.module.sass";
import AssetDisplay from "../../../ui/AssetDisplay";
import TradeButton from "../../../shared/trade-form/TradeButton";
import HideContentIcon from "../../mini-app/shared/HideContentIcon";
import AIAssistantServiceActivationModal from "../AIAssistantServiceActivationModal";
import { currencyFormatter } from "../../../utility/helper";
import { Table, Popover, Typography } from "antd";
import SignalMoreInfo from "./SignalMoreInfo";
import useSuggestedSignal from "../../../hooks/useSuggestedSignal";
import LiveAIButton from "../../new-dashboard/LiveAIButton";
import { openGuideChat } from "../../../shared/GuideChat";

const { Paragraph } = Typography;

const renderSignalIcon = (prediction) => {
  if (prediction === "Sell") return <FaArrowCircleDown size={16} />;
  if (prediction === "Buy") return <FaArrowCircleUp size={16} />;
  return <FaPauseCircle size={16} />;
};

const getSignalBadgeClass = (signal) => {
  if (signal?.toLowerCase().includes("buy")) return styles.buyBadge;
  if (signal?.toLowerCase().includes("sell")) return styles.sellBadge;
  return styles.holdBadge;
};

const SignalCell = ({ record, onInfoClick }) => {
  const { signal } = useSuggestedSignal({
    coin: record?.SYMBOL_NAME,
    oppositeSignal: "ai",
  });

  return (
    <div className={styles.signalContainer}>
      <span className={`${styles.signalBadge} ${getSignalBadgeClass(signal)}`}>
        <FaCircleInfo
          color="#2A86FF"
          style={{ marginRight: 4, cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            onInfoClick(record);
          }}
        />
        {signal}
      </span>
    </div>
  );
};

const StopLossCell = ({ record, isServiceActive, selectedTimeFrame }) => {
  const timeFrameData = record?.MT_SLTP?.[selectedTimeFrame];
  const stopLossPercent =
    timeFrameData?.STOP_LOSS_PERCENT || record?.STOP_LOSS_PERCENT;

  return isServiceActive ? (
    <Paragraph
      copyable={{
        text: `${Number(stopLossPercent)?.toFixed(2)}`,
        icon: [
          <FaCopy color="#0C4CFC" key="copy-icon" />,
          <FaCheck color="green" key="copied-icon" />,
        ],
        tooltips: ["Copy", "Copied!"],
      }}
      className={styles.copyableText}
    >
      {Number(stopLossPercent)?.toFixed(2)}%
    </Paragraph>
  ) : (
    <HideContentIcon />
  );
};

const TakeProfitCell = ({ record, isServiceActive, selectedTimeFrame }) => {
  const timeFrameData = record?.MT_SLTP?.[selectedTimeFrame];
  const takeProfitPercent =
    timeFrameData?.TAKE_PROFIT_PERCENT || record?.TAKE_PROFIT_PERCENT;

  return isServiceActive ? (
    <Paragraph
      copyable={{
        text: `${Number(takeProfitPercent)?.toFixed(2)}`,
        icon: [
          <FaCopy color="#0C4CFC" key="copy-icon" />,
          <FaCheck color="green" key="copied-icon" />,
        ],
        tooltips: ["Copy", "Copied!"],
      }}
      className={styles.copyableText}
    >
      {Number(takeProfitPercent)?.toFixed(2)}%
    </Paragraph>
  ) : (
    <HideContentIcon />
  );
};

const PositionSizeCell = ({
  record,
  isServiceActive,
  selectedTimeFrame,
  currentLeverage,
}) => {
  const { positionSize } = useSuggestedSignal({
    coin: record?.SYMBOL_NAME,
    oppositeSignal: "ai",
  });

  const timeFrameData = record?.MT_SLTP?.[selectedTimeFrame];
  const stopLossPercent =
    timeFrameData?.STOP_LOSS_PERCENT || record?.STOP_LOSS_PERCENT || 0;

  const calculatedPositionSize =
    stopLossPercent > 0 ? ((1 / stopLossPercent) * 100) / currentLeverage : 0;
  const displayPositionSize = calculatedPositionSize || positionSize || 0;

  const copyableText = `${displayPositionSize?.toFixed(2)}%`;
  const displayText = `${displayPositionSize?.toFixed(2)}%`;

  return isServiceActive ? (
    <Paragraph
      copyable={{
        text: copyableText,
        icon: [
          <FaCopy color="#0C4CFC" key="copy-icon" />,
          <FaCheck color="green" key="copied-icon" />,
        ],
        tooltips: ["Copy", "Copied!"],
      }}
      className={styles.copyableText}
    >
      {displayText}
    </Paragraph>
  ) : (
    <HideContentIcon />
  );
};

const PositionSizeCellWrapper = ({
  record,
  isServiceActive,
  selectedTimeFrame,
  getCurrentLeverage,
}) => {
  const { aiLeverage } = useSuggestedSignal({
    coin: record?.SYMBOL_NAME,
    oppositeSignal: "ai",
  });
  const currentLeverage = getCurrentLeverage(record, aiLeverage);

  return (
    <PositionSizeCell
      record={record}
      isServiceActive={isServiceActive}
      selectedTimeFrame={selectedTimeFrame}
      currentLeverage={currentLeverage}
    />
  );
};

const LeverageCellWrapper = ({
  record,
  isServiceActive,
  getCurrentLeverage,
  onLeverageChange,
  isExpandedRow = false,
}) => {
  const { aiLeverage } = useSuggestedSignal({
    coin: record?.SYMBOL_NAME,
    oppositeSignal: "ai",
  });
  const currentLeverage = getCurrentLeverage(record, aiLeverage);

  return (
    <LeverageCell
      record={record}
      isServiceActive={isServiceActive}
      currentLeverage={currentLeverage}
      onLeverageChange={onLeverageChange}
      isExpandedRow={isExpandedRow}
    />
  );
};

const LeverageCell = ({
  record,
  isServiceActive,
  currentLeverage,
  onLeverageChange,
  isExpandedRow = false,
}) => {
  const leverageOptions = [1, 2, 3, 4, 5, 6, 7];

  const popoverContent = (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: "6px",
        padding: "4px",
        minWidth: "120px",
      }}
    >
      {leverageOptions.map((leverage) => (
        <div
          key={leverage}
          onClick={() => onLeverageChange(record?.SYMBOL_NAME, leverage)}
          style={{
            padding: "6px",
            cursor: "pointer",
            backgroundColor:
              currentLeverage === leverage ? "#1890ff" : "#ffffff",
            color: currentLeverage === leverage ? "white" : "#333",
            borderRadius: "6px",
            textAlign: "center",
            fontSize: "12px",
            fontWeight: "500",
            border: currentLeverage === leverage ? "none" : "1px solid #e8e8e8",
            transition: "all 0.2s ease",
            boxShadow:
              currentLeverage === leverage
                ? "0 2px 4px rgba(24, 144, 255, 0.3)"
                : "none",
          }}
          onMouseEnter={(e) => {
            if (currentLeverage !== leverage) {
              e.target.style.backgroundColor = "#f5f5f5";
              e.target.style.borderColor = "#1890ff";
            }
          }}
          onMouseLeave={(e) => {
            if (currentLeverage !== leverage) {
              e.target.style.backgroundColor = "#ffffff";
              e.target.style.borderColor = "#e8e8e8";
            }
          }}
        >
          {leverage}x
        </div>
      ))}
    </div>
  );

  return isServiceActive ? (
    <div
      className={`${styles.leverageCell} ${isExpandedRow ? styles.leverageCellExpanded : ""}`}
    >
      <Popover
        content={popoverContent}
        title={null}
        trigger="click"
        placement="bottomLeft"
        overlayStyle={{ padding: 0 }}
      >
        <div
          className={`${styles.leverageButton} ${isExpandedRow ? styles.leverageButtonExpanded : ""}`}
        >
          {currentLeverage}x
        </div>
      </Popover>
    </div>
  ) : (
    <HideContentIcon />
  );
};

const ExpandedRowContent = ({
  data,
  isServiceActive,
  setShowServiceActivationModal,
  selectedTimeFrame,
  getCurrentLeverage,
  handleLeverageChange,
  showBacktest = true,
}) => {
  const { signal, positionSize, signalDate, aiLeverage } = useSuggestedSignal({
    coin: data?.SYMBOL_NAME,
    oppositeSignal: "ai",
  });

  const timeFrameData = data?.MT_SLTP?.[selectedTimeFrame];
  const stopLoss = timeFrameData?.STOP_LOSS_PERCENT || data?.STOP_LOSS_PERCENT;
  const takeProfit =
    timeFrameData?.TAKE_PROFIT_PERCENT || data?.TAKE_PROFIT_PERCENT;
  const currentLeverage = getCurrentLeverage(data, aiLeverage);
  const calculatedPositionSize =
    stopLoss > 0 ? ((1 / stopLoss) * 100) / currentLeverage : 0;
  const displayPositionSize = calculatedPositionSize || positionSize || 0;

  return (
    <div className={styles.expandedContent}>
      <div className={styles.expandedGrid}>
        {/* Signal More Info */}
        <div
          className={`${styles.expandedItem} ${styles.expandedItemSignalInfo}`}
        >
          <div className={styles.expandedLabel}>Signal Details</div>
          <div className={`${styles.expandedValue} ${styles.signalDetails}`}>
            <SignalMoreInfo data={data} />
          </div>
        </div>

        {/* Stop Loss - Show only when column is hidden (below lg breakpoint) */}
        <div
          className={`${styles.expandedItem} ${styles.expandedItemStopLoss}`}
        >
          <div className={styles.expandedLabel}>Stop Loss</div>
          <div className={styles.expandedValue}>
            <StopLossCell
              record={data}
              isServiceActive={isServiceActive}
              selectedTimeFrame={selectedTimeFrame}
            />
          </div>
        </div>

        {/* Take Profit - Show only when column is hidden (below lg breakpoint) */}
        <div
          className={`${styles.expandedItem} ${styles.expandedItemTakeProfit}`}
        >
          <div className={styles.expandedLabel}>Take Profit</div>
          <div className={styles.expandedValue}>
            <TakeProfitCell
              record={data}
              isServiceActive={isServiceActive}
              selectedTimeFrame={selectedTimeFrame}
            />
          </div>
        </div>

        {/* Position Size - Show only when column is hidden (below md breakpoint) */}
        <div
          className={`${styles.expandedItem} ${styles.expandedItemPositionSize}`}
        >
          <div className={styles.expandedLabel}>Position Size</div>
          <div className={styles.expandedValue}>
            <PositionSizeCellWrapper
              record={data}
              isServiceActive={isServiceActive}
              selectedTimeFrame={selectedTimeFrame}
              getCurrentLeverage={getCurrentLeverage}
            />
          </div>
        </div>

        {/* Leverage - Show only when column is hidden (below xl breakpoint) */}
        <div
          className={`${styles.expandedItem} ${styles.expandedItemLeverage}`}
        >
          <div className={styles.expandedLabel}>Leverage</div>
          <div className={styles.expandedValue}>
            <LeverageCellWrapper
              record={data}
              isServiceActive={isServiceActive}
              getCurrentLeverage={getCurrentLeverage}
              onLeverageChange={handleLeverageChange}
              isExpandedRow={true}
            />
          </div>
        </div>

        {/* Current Price - Show only when column is hidden (below md breakpoint) */}
        <div
          className={`${styles.expandedItem} ${styles.expandedItemCurrentPrice}`}
        >
          <div className={styles.expandedLabel}>Current Price</div>
          <div className={styles.expandedValue}>
            ${currencyFormatter(data?.CURRENT_PRICE)}
          </div>
        </div>

        {/* AI Backtest - Show only when screen is smaller than 576px */}
        {showBacktest && (
          <div
            className={`${styles.expandedItem} ${styles.expandedItemAiBacktest}`}
          >
            <div className={styles.expandedValue}>
              <LiveAIButton
                coin={data?.SYMBOL_NAME}
                exchange=""
                title="Run AI Backtest"
                iconColor="white"
                className={styles.aiBacktestButton}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TradeAssistantTable = ({ data = [] }) => {
  const [showServiceActivationModal, setShowServiceActivationModal] =
    useState(false);
  const [leverageState, setLeverageState] = useState({});
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const {
    tradingAssistant: { aiMarketType, selectedTimeFrame },
    profile: { profileData },
    liveSuggestion: { suggestions },
    otherCoinsAnalyze: { analyze },
  } = useSelector((state) => state);

  const isServiceActive = profileData?.myais_active === "True";

  const handleLeverageChange = (symbolName, newLeverage) => {
    setLeverageState((prev) => ({
      ...prev,
      [symbolName]: newLeverage,
    }));
  };

  const getCurrentLeverage = (record, aiLeverage) => {
    return leverageState[record?.SYMBOL_NAME] || aiLeverage || 1;
  };

  const handleInfoClick = (record) => {
    const key = record.key;
    if (expandedRowKeys.includes(key)) {
      // If already expanded, collapse it
      setExpandedRowKeys(expandedRowKeys.filter((k) => k !== key));
    } else {
      // If not expanded, expand it
      setExpandedRowKeys([...expandedRowKeys, key]);
    }
  };

  const getColumns = () => {
    const baseColumns = [
      {
        title: "Assets",
        dataIndex: "SYMBOL_NAME",
        key: "asset",
        width: 100,
        align: "center",
        render: (symbol) => {
          const isAnalyzed = analyze?.find(
            (signal) =>
              signal?.SYMBOL_NAME?.toLowerCase()?.split("/usdt")?.[0] ===
              symbol?.toLowerCase()?.split("/usdt")?.[0],
          );
          return (
            <div className={styles.asset}>
              <AssetDisplay
                className={styles.coinIcon}
                asset={symbol}
                isCrypto={aiMarketType === "crypto"}
              />
              {isAnalyzed && (
                <span className={styles.analyzedBadge}>Analyzed</span>
              )}
              <button
                type="button"
                className={styles.analyseCoin}
                onClick={() => openGuideChat({ deep: "markets", symbol })}
              >
                Analyse
              </button>
            </div>
          );
        },
      },
      {
        title: "Strength",
        key: "signal",
        align: "center",
        width: 115,
        render: (_, record) => {
          return isServiceActive ? (
            <SignalCell record={record} onInfoClick={handleInfoClick} />
          ) : (
            <HideContentIcon
              clickHandler={(e) => {
                e.stopPropagation();
                setShowServiceActivationModal(true);
              }}
            />
          );
        },
        sorter: (a, b) => {
          const aSymbol = a?.SYMBOL_NAME?.split("/usdt")?.[0];
          const bSymbol = b?.SYMBOL_NAME?.split("/usdt")?.[0];
          const aSignal = a?.TRADE_TYPE?.split("/")?.[0];
          const bSignal = b?.TRADE_TYPE?.split("/")?.[0];
          const aStrength = a?.SIGNAL_STRENGTH;
          const bStrength = b?.SIGNAL_STRENGTH;
          const aLiveData = suggestions?.find(
            (signal) => signal?.symbol?.split("/")?.[0] === aSymbol,
          );
          const bLiveData = suggestions?.find(
            (signal) => signal?.symbol?.split("/")?.[0] === bSymbol,
          );
          return (bSignal !== "Hold" && bStrength > aStrength) ||
            (bLiveData?.prediction !== "Hold" &&
              bLiveData?.confidence > aLiveData?.confidence)
            ? 1
            : -1;
          // console.log(
          //   aSignal,
          //   aStrength,
          //   aLiveData,
          //   bLiveData,
          //   bSignal,
          //   bStrength,
          // );
        },
      },
      {
        title: "Action",
        key: "actions",
        align: "center",
        width: 50,
        render: (_, record) => {
          const liveData = suggestions?.find(
            (s) => s?.symbol === record?.SYMBOL_NAME,
          );
          const prediction =
            aiMarketType === "crypto" &&
            liveData?.prediction &&
            liveData?.prediction !== "Hold"
              ? liveData?.prediction
              : record?.TRADE_TYPE?.split("/")?.[0];

          return (
            <TradeButton
              disabled={aiMarketType !== "crypto"}
              btnClassName={`${styles.tradeBtn} ${
                isServiceActive
                  ? prediction === "Buy"
                    ? styles.buyButton
                    : prediction === "Sell"
                      ? styles.sellButton
                      : styles.holdButton
                  : styles.hideContent
              }`}
              data={{ coin: record?.SYMBOL_NAME }}
              service="aiAssistant"
              onClick={() => {
                if (aiMarketType !== "crypto") return;
                if (!isServiceActive) {
                  return setShowServiceActivationModal(true);
                }
              }}
            >
              {!isServiceActive ? (
                <HideContentIcon style={{ padding: ".5rem" }} />
              ) : (
                <>
                  {/*{renderSignalIcon(prediction)}*/}
                  {/*{prediction}*/}
                  Trade
                </>
              )}
            </TradeButton>
          );
        },
      },
      {
        title: "Current Price",
        dataIndex: "CURRENT_PRICE",
        key: "price",
        align: "center",
        width: 95,
        responsive: ["md"],
        render: (price, record) =>
          isServiceActive ? (
            <span
              style={
                record?.priceColor ? { color: `${record?.priceColor}` } : {}
              }
              className={styles.priceValue}
            >
              ${currencyFormatter(price)}
            </span>
          ) : (
            <HideContentIcon />
          ),
      },
    ];

    // Add additional columns for larger screens
    const additionalColumns = [
      {
        title: "SL",
        key: "stopLoss",
        width: 80,
        align: "center",
        responsive: ["lg"],
        render: (_, record) => (
          <StopLossCell
            record={record}
            isServiceActive={isServiceActive}
            selectedTimeFrame={selectedTimeFrame}
          />
        ),
      },
      {
        title: "TP",
        key: "takeProfit",
        width: 80,
        align: "center",
        responsive: ["lg"],
        render: (_, record) => (
          <TakeProfitCell
            record={record}
            isServiceActive={isServiceActive}
            selectedTimeFrame={selectedTimeFrame}
          />
        ),
      },
      {
        title: "Position Size",
        key: "positionSize",
        width: 120,
        align: "center",
        responsive: ["md"],
        render: (_, record) => (
          <PositionSizeCellWrapper
            record={record}
            isServiceActive={isServiceActive}
            selectedTimeFrame={selectedTimeFrame}
            getCurrentLeverage={getCurrentLeverage}
          />
        ),
      },
      {
        title: "Leverage",
        key: "leverage",
        width: 100,
        align: "center",
        responsive: ["xl"],
        render: (_, record) => (
          <LeverageCellWrapper
            record={record}
            isServiceActive={isServiceActive}
            getCurrentLeverage={getCurrentLeverage}
            onLeverageChange={handleLeverageChange}
          />
        ),
      },
    ];

    if (aiMarketType === "crypto") {
      additionalColumns.push({
        title: "AI BackTest",
        key: "aiBacktest",
        width: 50,
        align: "center",
        responsive: ["sm"],
        render: (_, record) => (
          <LiveAIButton
            coin={record?.SYMBOL_NAME}
            exchange=""
            title="Run"
            iconColor="white"
            className={styles.aiBacktestButton}
          />
        ),
      });
    }

    return [...baseColumns, ...additionalColumns];
  };

  const tableData = data.map((item, index) => ({
    ...item,
    key: index,
  }));

  return (
    <div className={styles.tableContainer}>
      <Table
        columns={getColumns()}
        dataSource={tableData}
        pagination={false}
        expandable={{
          expandedRowKeys,
          onExpand: (expanded, record) => {
            if (expanded) {
              setExpandedRowKeys([...expandedRowKeys, record.key]);
            } else {
              setExpandedRowKeys(
                expandedRowKeys.filter((key) => key !== record.key),
              );
            }
          },
          expandedRowRender: (record) => (
            <ExpandedRowContent
              data={record}
              isServiceActive={isServiceActive}
              setShowServiceActivationModal={setShowServiceActivationModal}
              selectedTimeFrame={selectedTimeFrame}
              getCurrentLeverage={getCurrentLeverage}
              handleLeverageChange={handleLeverageChange}
              showBacktest={aiMarketType === "crypto"}
            />
          ),
          rowExpandable: () => true,
          className: styles.expandableRow,
          expandIcon: ({ expanded, onExpand, record }) =>
            expanded ? (
              <FaAngleUp
                onClick={(e) => onExpand(record, e)}
                style={{ cursor: "pointer", color: "#1890ff" }}
              />
            ) : (
              <FaAngleDown
                onClick={(e) => onExpand(record, e)}
                style={{ cursor: "pointer", color: "#1890ff" }}
              />
            ),
        }}
        className={styles.antTable}

        // size="middle"
        // scroll={{ x: "max-content" }}
      />

      <AIAssistantServiceActivationModal
        showActivationModal={showServiceActivationModal}
        setShowActivationModal={setShowServiceActivationModal}
      />
    </div>
  );
};

export default TradeAssistantTable;
